import { useCallback, useRef } from 'react'
import { useProductFetcher } from '../../../api/useProductFetcher/useProductFetcher'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import type {
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from '../../../types/types'
import {
    findAncestors,
    getChildElements,
} from '../../../utils/hoveredActionUtils'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { extractTextBeforeColon } from '../utils'

/**
 * Hook for filtering BOD nodes and syncing PT content from backend during PT activation.
 *
 * Provides:
 * 1. filterOutBODNodes - Removes BOD nodes and their children from activation lists
 * 2. syncPTContentToActivationList - Bulk-fetches PT article content from backend and
 *    syncs PT paragraph children (MFR, keyed content) to the activation list
 *
 * @returns Object with filterOutBODNodes and syncPTContentToActivationList
 */
export const useProductTypeContentActivation = () => {
    // Ref to store backend nodes from the bulk PT article fetch
    const backendPTNodesRef = useRef<Partial<ContentItemViewNodesType>[]>([])

    const { getContentItemViewNodesWithApolloClient } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            // Extract contentItemViewNodes from response
            const allBackendPTNodes =
                (response as GetProjectProductFamilyViewResponse)
                    .getProjectProductFamilyView?.contentItemViewNodes ||
                (response as GetProjectNuggetContentViewResponse)
                    .getProjectNugget?.contentItemViewNodes ||
                []

            // Store in ref so syncPTContentToActivationList can access it
            backendPTNodesRef.current = allBackendPTNodes
        },
    })

    /**
     * Filters out BOD (Basis of Design) nodes and their children from activation lists.
     * Mutates the provided Map by removing BOD-related entries.
     *
     * @param allItemsToActivate - Map of content item IDs to nodes (will be mutated)
     */
    const filterOutBODNodes = useCallback(
        (
            allItemsToActivate: Map<string, Partial<ContentItemViewNodesType>>
        ) => {
            const storeState = useWorkspace2Store.getState()
            const latestNodes = storeState.contentItemViewNodes

            // Find all BOD node IDs and their children
            const bodNodeIds = new Set<string>()
            allItemsToActivate.forEach((item) => {
                if (item.isBOD) {
                    // Add the BOD node itself
                    if (item.id) bodNodeIds.add(item.id)

                    // Add all children of this BOD node
                    const children = getChildElements(item, latestNodes)
                    children.forEach((child) => {
                        if (child.id) bodNodeIds.add(child.id)
                    })
                }
            })

            // Remove BOD nodes and their children from activation list
            bodNodeIds.forEach((id) => {
                allItemsToActivate.delete(id)
            })
        },
        []
    )

    /**
     * Syncs PT paragraph children (MFR paragraphs, keyed content) from backend
     * to the activation list after PT is added to project.
     *
     * How it works:
     * 1. Finds all PT key paragraphs in frontend state for all PT names
     * 2. If none found, skips entirely — no fetch needed
     * 3. Collects unique parent article IDs from all PT key paragraphs
     * 4. Makes ONE bulk API call with all article IDs
     * 5. For each PT key paragraph, replaces nodes from that paragraph through
     *    its last child with fresh backend data in allItemsToActivate
     *
     * Sequential — no race conditions.
     * Non-throwing — PT activation continues even if this sync fails.
     *
     * @param allItemsToActivate - Map to add synced PT content to (mutated in place)
     * @param ptNames - PT names being activated (e.g., ["Concrete", "Steel"])
     */
    const syncPTContentToActivationList = useCallback(
        async (
            allItemsToActivate: Map<string, Partial<ContentItemViewNodesType>>,
            ptNames: string[],
            idsToRemove?: Set<string>
        ) => {
            try {
                const latestNodes = useWorkspace2Store.getState()
                    .contentItemViewNodes

                // Step 1: Collect all PT key paragraphs across all PT names
                const allPTKeyParagraphs: Partial<
                    ContentItemViewNodesType
                >[] = []
                for (const ptName of ptNames) {
                    const matches = latestNodes.filter((node) => {
                        const text = node.elements?.[0]?.text
                        if (!text?.includes(':') || !node.isProductType)
                            return false
                        return (
                            extractTextBeforeColon(text || '').toLowerCase() ===
                            ptName.toLowerCase()
                        )
                    })
                    allPTKeyParagraphs.push(...matches)
                }

                // Step 2: No PT paragraphs found — skip, no fetch needed
                if (allPTKeyParagraphs.length === 0) return

                // Step 3: Collect unique parent article IDs for all PT key paragraphs
                // Only include articles that are productGroup or productType
                const articleIdSet = new Set<string>()
                for (const ptParagraph of allPTKeyParagraphs) {
                    const parentArticle = findAncestors(
                        latestNodes,
                        ptParagraph
                    ).find((a) => a.textType === 'Article')
                    if (
                        parentArticle?.id &&
                        (parentArticle.isProductGroup ||
                            parentArticle.isProductType)
                    ) {
                        articleIdSet.add(parentArticle.id)
                    }
                }

                if (articleIdSet.size === 0) return

                // Step 4: Single bulk API call with all article IDs
                await getContentItemViewNodesWithApolloClient(
                    Array.from(articleIdSet)
                )

                const backendNodes = backendPTNodesRef.current
                if (backendNodes.length === 0) return

                // Step 5: For each PT key paragraph, build the full range using
                // backend data so NEW nodes (not yet in frontend) are included
                for (const ptKeyParagraph of allPTKeyParagraphs) {
                    if (!ptKeyParagraph.id) continue

                    // Find the PT key paragraph in the backend response
                    const backendPTKeyParagraph = backendNodes.find(
                        (n) => n.id === ptKeyParagraph.id
                    )
                    if (!backendPTKeyParagraph) continue

                    // Mark existing frontend manufacturer children for removal — they will be
                    // replaced by the backend versions to avoid duplicate nodes.
                    // Skip BOD nodes — they are not re-added from backend and must stay in store.
                    if (idsToRemove) {
                        const frontendChildren = getChildElements(
                            ptKeyParagraph,
                            latestNodes
                        )
                        frontendChildren.forEach((child) => {
                            if (child.id && !child.isBOD)
                                idsToRemove.add(child.id)
                        })
                    }

                    // Build range from backend data: PT paragraph + all backend children
                    // This captures NEW nodes (MFR, keyed content) that don't exist
                    // in the frontend yet but are present in the backend after PT addition
                    const backendChildren = getChildElements(
                        backendPTKeyParagraph,
                        backendNodes
                    )
                    const rangeNodes = [
                        backendPTKeyParagraph,
                        ...backendChildren,
                    ]

                    for (const backendNode of rangeNodes) {
                        if (backendNode?.id && backendNode.isHidden === false) {
                            allItemsToActivate.set(backendNode.id, backendNode)
                        }
                    }
                }
            } catch (error) {
                console.error(
                    '[useProductTypeContentActivation] Failed to sync PT content from backend:',
                    error
                )
                // Don't throw — PT activation continues without this sync
            } finally {
                backendPTNodesRef.current = []
            }
        },
        [getContentItemViewNodesWithApolloClient]
    )

    return {
        filterOutBODNodes,
        syncPTContentToActivationList,
    }
}
